/**
 * Role reference parsing utilities
 * Handles extraction of role references from text
 * New format: @[roleId:123]
 * Legacy format (backward compatibility): @[Role Name](roleId:123)
 */

/**
 * Parse role references from text and extract role IDs
 * @param text - Text containing potential role references
 * @returns Array of role IDs found in text
 */
export function parseRoleReferences(text: string): number[] {
    if (!text) return [];
    
    const roleIds: number[] = [];
    
    // New format: @[roleId:123]
    const newPattern = /@\[roleId:(\d+)\]/g;
    let match;
    while ((match = newPattern.exec(text)) !== null) {
        roleIds.push(parseInt(match[1], 10));
    }
    
    // Legacy format: @[anything](roleId:123) - for backward compatibility
    const legacyPattern = /@\[.+?\]\(roleId:(\d+)\)/g;
    while ((match = legacyPattern.exec(text)) !== null) {
        roleIds.push(parseInt(match[1], 10));
    }
    
    return roleIds;
}

/**
 * Extract all unique role references from purpose, accountabilities, and responsibilities
 * @param purpose - Purpose text
 * @param accountabilities - Array of accountability strings
 * @param responsibilities - Array of responsibility strings
 * @returns Array of unique role IDs
 */
export function extractAllReferences(
    purpose: string,
    accountabilities: string[],
    responsibilities: string[]
): number[] {
    const allText = [
        purpose || '',
        ...(accountabilities || []),
        ...(responsibilities || [])
    ].join(' ');
    
    const roleIds = parseRoleReferences(allText);
    return [...new Set(roleIds)]; // Deduplicate
}

/**
 * Render role references in text as clickable HTML elements
 * @param text - Text containing role references
 * @param roleMap - Map of role ID to current role name (required for proper rendering)
 * @returns HTML string with references as clickable elements
 */
export function renderRoleReferences(text: string, roleMap?: Map<number, string>): string {
    if (!text) return '';
    
    // Helper to escape HTML special characters to prevent XSS
    const escapeHtml = (str: string): string => {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    };
    
    let result = text;
    
    // Handle new format: @[roleId:123]
    result = result.replace(/@\[roleId:(\d+)\]/g, (match, roleIdStr) => {
        const roleId = parseInt(roleIdStr, 10);
        const displayName = roleMap?.get(roleId) || `[Role #${roleId}]`;
        // Escape the display name to prevent XSS via compromised role names
        const escapedName = escapeHtml(displayName);
        return `<span class="role-reference role-link" data-role-id="${roleId}">${escapedName}</span>`;
    });
    
    // Handle legacy format: @[Role Name](roleId:123) - for backward compatibility
    // Always use current name from roleMap if available
    result = result.replace(/@\[(.+?)\]\(roleId:(\d+)\)/g, (match, storedName, roleIdStr) => {
        const roleId = parseInt(roleIdStr, 10);
        const displayName = roleMap?.get(roleId) || storedName;
        // Escape the display name to prevent XSS
        const escapedName = escapeHtml(displayName);
        return `<span class="role-reference role-link" data-role-id="${roleId}">${escapedName}</span>`;
    });
    
    return result;
}
